import { WPN_POPUP_STATUS } from '../global-store/subjects/auto-push-notifications/get-actions';
import logger from '../services/logging/logging-service';

import { forEach } from './array-utils';
import { setStatusOnLocalStorage, WPN_STATUS, WPN_SUBSCRIPTION } from './local-storage';
import { printObject } from './object-utils';

export const initDefaultOptions = ({ selectBoxes, actions, allUnchecked = false }) => {
  const selectedOptions = {};
  selectBoxes?.forEach((selectBox) => {
    const tags = allUnchecked
      ? []
      : selectBox.options.filter((option) => option.defaultChecked).map((option) => option.value);
    selectedOptions[selectBox.boxName] = tags;
    actions.setSelectedOptions(selectBox.boxName, tags);
  });
};

export const showPreferencesDialog = ({ notificationPermission, selectBoxes, actions }) => {
  if (notificationPermission === 'granted') {
    initDefaultOptions({ selectBoxes, actions });
    actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_PREFERENCES_DIALOG);
    logger.info('[WPN] show FAB, notificationPermission granted');
  } else if (notificationPermission === 'denied') {
    actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
    logger.info('[WPN] FAB, permission denied');
  }
};

export const optOut = async (sdk) => {
  const response = await sdk?.channel?.optOut();
  if (!response) {
    throw new Error('UA Optout call failed');
  }
  setStatusOnLocalStorage({
    key: WPN_STATUS.OBJECT_KEY,
    name: WPN_STATUS.UNSUBSCRIBED.NAME,
    expirationDays: WPN_STATUS.UNSUBSCRIBED.EXPIRATION_DAYS,
  });
  setStatusOnLocalStorage({ key: WPN_SUBSCRIPTION.OBJECT_KEY, name: WPN_SUBSCRIPTION.UNSUBSCRIBED.NAME });
};

export const setOptionsFromSdk = ({ sdk, selectBoxes, actions }) => {
  selectBoxes?.forEach((selectBox) => {
    const optionsFromSdk = selectBox.options
      .map((option) => option.value)
      .filter((tagValue) => sdk?.channel?.tags.has(tagValue));

    actions.setSelectedOptions(selectBox.boxName, optionsFromSdk);
  });
};

const resetOptionsOnSdk = (sdk) => {
  sdk?.channel?.tags?.list?.device.forEach((tag) => {
    sdk.channel.tags.remove(tag);
  });
};

export const setOptionsToSdk = async ({ sdk, selectBoxes, selectedOptions }) => {
  resetOptionsOnSdk(sdk);
  try {
    if (!sdk?.channel?.optedIn) {
      await sdk?.register();
      setStatusOnLocalStorage({ key: WPN_SUBSCRIPTION.OBJECT_KEY, name: WPN_SUBSCRIPTION.SUBSCRIBED.NAME });
      logger.info('[WPN] UA - Subscribe to web push notification');
    }
    forEach((selectBox) => {
      forEach((selectedOption) => sdk?.channel?.tags?.add(selectedOption))(selectedOptions[selectBox.boxName]);
    })(selectBoxes);
    if (sdk?.channel) {
      logger.info(`[WPN] UA - accepted - SkySport Channel ID ${sdk?.channel.id}`);
      logger.info(`[WPN] UA - accepted - Channel Tags ${printObject(sdk?.channel.tags.list)}`);
    }
  } catch (error) {
    throw new Error(`[WPN] [UA register] There has been a problem with push notification: `, error?.message || error);
  }
};

/**
 * @param {array} selectBoxes
 * @param {object} selectedOptions
 * @returns {string} i.e. "squadre|sport"
 */
export const getAnalyticsCategories = (selectBoxes, selectedOptions) =>
  Object.keys(selectedOptions)
    .filter((selectedOptionKey) => Boolean(selectedOptions[selectedOptionKey].length))
    .map(
      (selectedOptionKey) =>
        selectBoxes.filter((selectBox) => selectBox.boxName === selectedOptionKey)[0]?.analyticsCategory
    )
    .join('|');

/**
 * @param {array} selectBoxes
 * @param {object} selectedOptions
 * @returns {string} i.e. "calc|bskt|int|juve"
 */
export const getAnalyticsTopics = (selectBoxes, selectedOptions) =>
  Object.entries(selectedOptions)
    .map(([selectedKey, selectedOptionsArray]) =>
      selectedOptionsArray.map((selectedOption) => {
        const analyticsTopic = selectBoxes
          .filter((selectBox) => selectBox.boxName === selectedKey)[0]
          ?.options.filter((option) => option.value === selectedOption)[0]?.analyticsTopic;
        return analyticsTopic;
      })
    )
    .reduce((prev, curr) => [...prev, ...curr], [])
    .filter((topic) => Boolean(topic))
    .join('|');
